import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import {
  FiRepeat,
  FiPlus,
  FiSearch,
  FiFilter,
  FiPlay,
  FiPause,
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiLoader
} from 'react-icons/fi';
import RecurringInvoiceForm from './recurring/RecurringInvoiceForm';
import { getStatusColor } from '../utils/recurringLogic';
import { recurringService } from '../services/recurringService';
import { formatCurrency } from '../utils/formatters';
import { useClient } from '../context/ClientContext';

export default function RecurringInvoices() {
  const { activeClient } = useClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const fetchSchedules = async () => {
    if (!activeClient?.id) return;
    setIsLoading(true);
    try {
      const data = await recurringService.getSchedules(activeClient.id);
      setSchedules(data || []);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeClient?.id) {
      fetchSchedules();
    } else {
      setSchedules([]);
      setIsLoading(false);
    }
  }, [activeClient?.id]);

  const handleEdit = (schedule) => {
    setSelectedSchedule(schedule);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedSchedule(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this recurring schedule?')) {
      try {
        await recurringService.deleteSchedule(id);
        fetchSchedules();
      } catch (error) {
        console.error('Failed to delete schedule:', error);
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Paused' : 'Active';
    try {
      await recurringService.toggleStatus(id, newStatus);
      fetchSchedules();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const filteredSchedules = schedules.filter((schedule) =>
    schedule.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = schedules.filter((schedule) => schedule.status === 'Active').length;
  const monthlyRevenue = schedules
    .filter((schedule) => schedule.status === 'Active')
    .reduce((sum, schedule) => {
      let multiplier = 1;
      if (schedule.frequency === 'weekly') multiplier = 4.33;
      else if (schedule.frequency === 'quarterly') multiplier = 0.33;
      else if (schedule.frequency === 'annually') multiplier = 0.083;
      return sum + (Number(schedule.amount) * multiplier);
    }, 0);

  const today = new Date();
  const nextWeek = new Date();
  nextWeek.setDate(today.getDate() + 7);

  const upcomingCount = schedules.filter((schedule) => {
    if (schedule.status !== 'Active' || !schedule.next_date) return false;
    const nextDate = new Date(schedule.next_date);
    return nextDate >= today && nextDate <= nextWeek;
  }).length;

  const autosendCount = schedules.filter(
    (schedule) => schedule.auto_send && schedule.status === 'Active'
  ).length;
  const autosendRate = activeCount > 0 ? Math.round((autosendCount / activeCount) * 100) : 0;

  return (
    <div className="mx-auto max-w-[1500px] animate-in fade-in duration-500">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="flex items-center gap-2.5 text-[23px] font-semibold tracking-tight text-slate-900">
            <SafeIcon icon={FiRepeat} className="text-[22px] text-emerald-600" />
            Recurring Invoices
          </h1>
          <p className="mt-1 text-[13px] font-normal text-slate-500">
            Automated billing schedules for your long-term clients.
          </p>
        </div>

        <button
          onClick={() => {
            setSelectedSchedule(null);
            setIsFormOpen(true);
          }}
          className="flex items-center justify-center gap-2 rounded-xl bg-[#22c55e] px-4 py-2.5 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-green-600"
        >
          <SafeIcon icon={FiPlus} />
          Create Schedule
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            Active Schedules
          </p>
          <div className="text-[27px] font-semibold leading-tight text-slate-900">{activeCount}</div>
          <p className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-emerald-600">
            <SafeIcon icon={FiPlay} className="text-[13px]" />
            Generating {formatCurrency(monthlyRevenue)}/mo
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            Upcoming Invoices (7d)
          </p>
          <div className="text-[27px] font-semibold leading-tight text-slate-900">{upcomingCount}</div>
          <p className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-blue-600">
            <SafeIcon icon={FiCalendar} className="text-[13px]" />
            {upcomingCount === 0 ? 'Review queue is clear' : 'Action may be needed'}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            Autosend Rate
          </p>
          <div className="text-[27px] font-semibold leading-tight text-slate-900">{autosendRate}%</div>
          <p className="mt-2 text-[12px] font-normal text-slate-500">Zero-touch billing enabled</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 bg-slate-50/70 px-5 py-4">
          <div className="relative w-full max-w-md">
            <SafeIcon
              icon={FiSearch}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[16px] text-slate-400"
            />
            <input
              type="text"
              placeholder="Filter schedules..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-[13px] font-normal text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/10"
            />
          </div>

          <button
            className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600"
            title="Filter schedules"
          >
            <SafeIcon icon={FiFilter} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex h-56 flex-col items-center justify-center text-slate-400">
            <SafeIcon icon={FiLoader} className="mb-3 text-2xl animate-spin" />
            <p className="text-[12px] font-medium tracking-wide">Loading schedules...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead className="border-b border-slate-200 bg-slate-50 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400">
                <tr>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5">Customer</th>
                  <th className="px-5 py-3.5">Frequency</th>
                  <th className="px-5 py-3.5">Amount</th>
                  <th className="px-5 py-3.5">Next Occurrence</th>
                  <th className="px-5 py-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-[13px]">
                {filteredSchedules.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-14 text-center font-normal text-slate-500">
                      No recurring schedules found.
                    </td>
                  </tr>
                ) : (
                  filteredSchedules.map((schedule) => (
                    <tr
                      key={schedule.id}
                      className="group border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                    >
                      <td className="px-5 py-3.5">
                        <span
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide ${getStatusColor(schedule.status)}`}
                        >
                          {schedule.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 font-medium text-slate-800">
                        {schedule.customer?.name || 'Unknown'}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 font-normal capitalize text-slate-600">
                          <SafeIcon icon={FiRepeat} className="text-[14px] text-emerald-500" />
                          {schedule.frequency}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 font-medium text-slate-800">
                        {formatCurrency(schedule.amount)}
                      </td>
                      <td className="px-5 py-3.5 font-normal text-slate-500">
                        {schedule.next_date || 'N/A'}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(schedule)}
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                            title="Edit"
                          >
                            <SafeIcon icon={FiEdit} />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(schedule.id, schedule.status)}
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-amber-50 hover:text-amber-600"
                            title={schedule.status === 'Active' ? 'Pause' : 'Resume'}
                          >
                            <SafeIcon icon={schedule.status === 'Active' ? FiPause : FiPlay} />
                          </button>
                          <button
                            onClick={() => handleDelete(schedule.id)}
                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isFormOpen && (
        <RecurringInvoiceForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          initialData={selectedSchedule}
          onSuccess={fetchSchedules}
        />
      )}
    </div>
  );
}